import { createClient } from '@supabase/supabase-js'
import { SessionContextProvider } from '@supabase/auth-helpers-react'
import { useEffect, useState } from 'react'
import { MantineProvider } from '@mantine/core'
import { ModalsProvider } from '@mantine/modals'
import { Notifications } from '@mantine/notifications'

import classNames from 'classnames'
import RouterLink from '@/components/ui/RouterLink'
import { Provider as BusProvider } from 'react-bus'

// Query params
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6'
import { QueryParamProvider } from 'use-query-params'

// i18n
import '@/i18n'
import { useTranslation } from 'react-i18next'

// Router
import {
    BrowserRouter,
    Routes,
    Route,
    Navigate,
    Outlet,
} from 'react-router-dom'

// Layout
import BackofficeLayout from '@/components/layouts/BackofficeLayout'
import AuthGuard from '@/components/auth/AuthGuard'
import RoleGuard from '@/components/auth/RoleGuard'
import NotFound from '@/components/ui/NotFound'

// PWA iOS Keyboard fix
import useIOSKeyboardFix from '@/hooks/useIOSKeyboardFix'

// Dayjs setup
import dayjs from 'dayjs'
import LocalizedFormat from 'dayjs/plugin/localizedFormat'
import Calendar from 'dayjs/plugin/calendar'
import updateLocale from 'dayjs/plugin/updateLocale'
import 'dayjs/locale/fr'

dayjs.extend(LocalizedFormat)
dayjs.extend(Calendar)
dayjs.extend(updateLocale)

dayjs.locale('fr')

dayjs.updateLocale('fr', {
    calendar: {
        lastDay: '[hier à] LT',
        sameDay: "[aujourd'hui à] LT",
        nextDay: '[demain à] LT',
        lastWeek: 'dddd [dernier à] LT',
        nextWeek: 'dddd [prochain à] LT',
        sameElse: 'dddd ll à LT',
    },
})

// Pages — no layout
import RedirectAfterMagicLink from '@/pages/redirect_after_magic_link'
import OAuthConsentPage from '@/pages/oauth/consent'
import RetailersProductsPage from '@/pages/retailers-products/index'

// Pages — with layout
import DashboardPage from '@/pages/index'
import ChangelogPage from '@/pages/changelog'
import MePage from '@/pages/me'

// Assembly
import AssemblyIndexPage from '@/pages/assembly/index'
import AssemblyCalendarPage from '@/pages/assembly/calendar'
import AssemblyCreatePage from '@/pages/assembly/create'
import AssemblyCreateNewPage from '@/pages/assembly/create-new'
import AssemblyDetailPage from '@/pages/assembly/[production_order_id]'

// Components
import ComponentsIndexPage from '@/pages/components/index'
import ComponentsHistoryPage from '@/pages/components/history'
import ComponentDetailPage from '@/pages/components/[component_id]'

// Fulfillment Orders
import FulfillmentOrdersIndexPage from '@/pages/fulfillment_orders/index'
import FulfillmentOrdersCreatePage from '@/pages/fulfillment_orders/create'
import FulfillmentOrdersDispatchPage from '@/pages/fulfillment_orders/dispatch'
import FulfillmentOrdersReallocationPage from '@/pages/fulfillment_orders/reallocation'
import FulfillmentOrderDetailPage from '@/pages/fulfillment_orders/[fulfillment_order_id]'

// Fulfillments
import FulfillmentsIndexPage from '@/pages/fulfillments/index'

// Orders
import OrderDetailPage from '@/pages/orders/[order_id]'
import CancellationRequestsPage from '@/pages/orders/cancellation-requests'

// Products
import ShopifyProductsIndexPage from '@/pages/products/shopify/index'
import ShopifyProductDetailPage from '@/pages/products/shopify/[product_id]'
import ShopifyVariantsPage from '@/pages/products/shopify/variants'
import ProductsAvailabilityPage from '@/pages/products/availability'

// Backups
import BackupsPage from '@/pages/backups/index'

// Profiles
import ProfilesIndexPage from '@/pages/profiles/index'
import ProfileDetailPage from '@/pages/profiles/[profile_id]'

// Purchase Orders
import PurchaseOrdersIndexPage from '@/pages/purchase_orders/index'
import PurchaseOrdersCreatePage from '@/pages/purchase_orders/create'
import PurchaseOrderDetailPage from '@/pages/purchase_orders/[id]'

// Retailer Estimates
import RetailerEstimatesIndexPage from '@/pages/retailer_estimates/index'
import RetailerEstimatesCreatePage from '@/pages/retailer_estimates/create'
import RetailerEstimateDetailPage from '@/pages/retailer_estimates/[id]'

// Retailer Invoices
import RetailerInvoicesIndexPage from '@/pages/retailer_invoices/index'
import RetailerInvoiceDetailPage from '@/pages/retailer_invoices/[id]'

// Retailers
import RetailersIndexPage from '@/pages/retailers/index'
import RetailerDetailPage from '@/pages/retailers/[retailer_id]'
import RetailersStatsPage from '@/pages/retailers/stats/index'

// Service Cases
import ServicesIndexPage from '@/pages/service-cases/index'
import ServiceCaseCreatePage from '@/pages/service-cases/create'
import ServiceDetailPage from '@/pages/service-cases/[id]'
import ServiceCaseWorkflowPage from '@/pages/service-cases/workflow'

// Support IA
import SupportPoliciesPage from '@/pages/support/ai/policies'
import SupportAiStatsPage from '@/pages/support/ai/stats'
import SupportConversationsPage from '@/pages/support/ai/conversations'

// Shipments
import ShipmentsIndexPage from '@/pages/shipments/index'

// Stock
import ReplenishmentRequestPage from '@/pages/stock/replenishment-request'

// Warranties
import WarrantiesIndexPage from '@/pages/warranties/index'
import WarrantyDetailPage from '@/pages/warranties/[code]'
import WarrantyAssociatePage from '@/pages/warranties/associate'

// Supabase client (singleton)
const supabaseClient = createClient(
    import.meta.env.VITE_SUPABASE_URL,
    import.meta.env.VITE_SUPABASE_ANON_KEY
)

const mantineTheme = {
    fontFamily: 'Inter, sans-serif',
    defaultRadius: 12,
    colorScheme: 'light',
    cursorType: 'pointer',
    colors: {
        brand: [
            '#B4BCCB',
            '#9BA9C0',
            '#8397BA',
            '#6886B7',
            '#4B75BA',
            '#3566B9',
            '#2676fd',
            '#2B5497',
            '#314E7E',
            '#33486B',
            '#33425B',
            '#313C4E',
            '#2F3744',
        ],
    },
    primaryColor: 'brand',
    components: {
        Anchor: {
            defaultProps: {
                component: RouterLink,
            },
        },
    },
}

function AppProviders({ children }) {
    // Set i18n language from localStorage
    const { i18n } = useTranslation()

    useEffect(() => {
        const language = localStorage.getItem('language')
        if (language) {
            i18n.changeLanguage(language)
        }
    }, [])

    // PWA iOS Keyboard fix
    useIOSKeyboardFix()

    return (
        <SessionContextProvider supabaseClient={supabaseClient}>
            <MantineProvider theme={mantineTheme} defaultColorScheme="light">
                <BusProvider>
                    <QueryParamProvider
                        adapter={ReactRouter6Adapter}
                        options={{ removeDefaultsFromUrl: true }}
                    >
                        <ModalsProvider>
                            <Notifications />
                            {children}
                        </ModalsProvider>
                    </QueryParamProvider>
                </BusProvider>
            </MantineProvider>
        </SessionContextProvider>
    )
}

function ProtectedLayout() {
    return (
        <AuthGuard>
            <div>
                <BackofficeLayout>
                    <Outlet />
                </BackofficeLayout>
            </div>
        </AuthGuard>
    )
}

export default function App() {
    return (
        <BrowserRouter>
            <AppProviders>
                <Routes>
                    {/* Routes without layout */}
                    <Route
                        path="/login"
                        element={<Navigate to="/" replace />}
                    />
                    <Route
                        path="/redirect_after_magic_link"
                        element={<RedirectAfterMagicLink />}
                    />
                    {/* OAuth consent (Supabase OAuth 2.1 server). AuthGuard
                        renders the login page in place when there is no
                        session, keeping ?authorization_id intact. */}
                    <Route
                        path="/oauth/consent"
                        element={
                            <AuthGuard>
                                <OAuthConsentPage />
                            </AuthGuard>
                        }
                    />

                    {/* Routes with layout + auth guard */}
                    <Route element={<ProtectedLayout />}>
                        <Route path="/" element={<DashboardPage />} />
                        <Route path="/changelog" element={<ChangelogPage />} />
                        <Route path="/me" element={<MePage />} />

                        {/* Assembly */}
                        <Route
                            path="/assembly"
                            element={<AssemblyIndexPage />}
                        />
                        <Route
                            path="/assembly/calendar"
                            element={<AssemblyCalendarPage />}
                        />
                        <Route
                            path="/assembly/create"
                            element={<AssemblyCreatePage />}
                        />
                        <Route
                            path="/assembly/create-new"
                            element={<AssemblyCreateNewPage />}
                        />
                        <Route
                            path="/assembly/:production_order_id"
                            element={<AssemblyDetailPage />}
                        />

                        {/* Components */}
                        <Route
                            path="/components"
                            element={<ComponentsIndexPage />}
                        />
                        <Route
                            path="/components/history"
                            element={<ComponentsHistoryPage />}
                        />
                        <Route
                            path="/components/:component_id"
                            element={<ComponentDetailPage />}
                        />

                        {/* Fulfillment Orders */}
                        <Route
                            path="/fulfillment_orders"
                            element={<FulfillmentOrdersIndexPage />}
                        />
                        <Route
                            path="/fulfillment_orders/create"
                            element={<FulfillmentOrdersCreatePage />}
                        />
                        <Route
                            path="/fulfillment_orders/dispatch"
                            element={<FulfillmentOrdersDispatchPage />}
                        />
                        <Route
                            path="/fulfillment_orders/reallocation"
                            element={<FulfillmentOrdersReallocationPage />}
                        />
                        <Route
                            path="/fulfillment_orders/:fulfillment_order_id"
                            element={<FulfillmentOrderDetailPage />}
                        />

                        {/* Fulfillments */}
                        <Route
                            path="/fulfillments"
                            element={<FulfillmentsIndexPage />}
                        />

                        {/* Orders */}
                        <Route
                            path="/orders/:order_id"
                            element={<OrderDetailPage />}
                        />

                        {/* Admin only — Commerce */}
                        <Route element={<RoleGuard roles={['ADMIN']} />}>
                            <Route
                                path="/orders/cancellation-requests"
                                element={<CancellationRequestsPage />}
                            />
                            {/* Support IA */}
                            <Route
                                path="/support/ai"
                                element={
                                    <Navigate
                                        to="/support/ai/policies"
                                        replace
                                    />
                                }
                            />
                            <Route
                                path="/support/ai/policies/:id?"
                                element={<SupportPoliciesPage />}
                            />
                            <Route
                                path="/support/ai/conversations/:id?"
                                element={<SupportConversationsPage />}
                            />
                            <Route
                                path="/support/ai/stats"
                                element={<SupportAiStatsPage />}
                            />

                            {/* Products */}
                            {/* Reached by URL only: no sidebar entry */}
                            <Route
                                path="/products/availability"
                                element={<ProductsAvailabilityPage />}
                            />
                            <Route
                                path="/products/shopify"
                                element={<ShopifyProductsIndexPage />}
                            />
                            <Route
                                path="/products/shopify/variants"
                                element={<ShopifyVariantsPage />}
                            />
                            <Route
                                path="/products/shopify/:product_id"
                                element={<ShopifyProductDetailPage />}
                            />

                            {/* Backups */}
                            <Route
                                path="/backups"
                                element={<BackupsPage />}
                            />

                            {/* Profiles */}
                            <Route
                                path="/profiles"
                                element={<ProfilesIndexPage />}
                            />
                            <Route
                                path="/profiles/:profile_id"
                                element={<ProfileDetailPage />}
                            />

                            {/* Retailer Estimates */}
                            <Route
                                path="/retailer_estimates"
                                element={<RetailerEstimatesIndexPage />}
                            />
                            <Route
                                path="/retailer_estimates/create"
                                element={<RetailerEstimatesCreatePage />}
                            />
                            <Route
                                path="/retailer_estimates/:id"
                                element={<RetailerEstimateDetailPage />}
                            />

                            {/* Retailer Invoices */}
                            <Route
                                path="/retailer_invoices"
                                element={<RetailerInvoicesIndexPage />}
                            />
                            <Route
                                path="/retailer_invoices/:id"
                                element={<RetailerInvoiceDetailPage />}
                            />

                            {/* Retailers */}
                            <Route
                                path="/retailers"
                                element={<RetailersIndexPage />}
                            />
                            <Route
                                path="/retailers/stats"
                                element={<RetailersStatsPage />}
                            />
                            <Route
                                path="/retailers/:retailer_id"
                                element={<RetailerDetailPage />}
                            />
                        </Route>

                        {/* Purchase Orders */}
                        <Route
                            path="/purchase_orders"
                            element={<PurchaseOrdersIndexPage />}
                        />
                        <Route
                            path="/purchase_orders/create"
                            element={<PurchaseOrdersCreatePage />}
                        />
                        <Route
                            path="/purchase_orders/:id"
                            element={<PurchaseOrderDetailPage />}
                        />

                        {/* Service Cases */}
                        <Route
                            path="/service-cases"
                            element={<ServicesIndexPage />}
                        />
                        <Route
                            path="/service-cases/create"
                            element={<ServiceCaseCreatePage />}
                        />
                        <Route
                            path="/service-cases/workflow"
                            element={<ServiceCaseWorkflowPage />}
                        />
                        <Route
                            path="/service-cases/:id"
                            element={<ServiceDetailPage />}
                        />

                        {/* Shipments */}
                        <Route
                            path="/shipments"
                            element={<ShipmentsIndexPage />}
                        />

                        {/* Stock */}
                        <Route
                            path="/stock/replenishment-request"
                            element={<ReplenishmentRequestPage />}
                        />

                        {/* Warranties */}
                        <Route
                            path="/warranties"
                            element={<WarrantiesIndexPage />}
                        />
                        <Route
                            path="/warranties/associate"
                            element={<WarrantyAssociatePage />}
                        />
                        <Route
                            path="/warranties/:code"
                            element={<WarrantyDetailPage />}
                        />

                        {/* 404 */}
                        <Route path="*" element={<NotFound />} />
                    </Route>
                </Routes>
            </AppProviders>
        </BrowserRouter>
    )
}
